import type { GenericAbortSignal } from 'axios';
import axios from 'axios';

import { DictTypes, type DictTypesKeys } from '@/shared/constants';
import type { GetDictContentType } from '@/shared/types/dicts.types';

import { type CustomAxiosError, axiosInstance } from './index.api';

export class ApiConnector {
  async call<Request, Response>({
    path,
    method = 'get',
    body,
    params,
    abortSignal,
    responseType
  }: {
    path: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
    body?: Request;
    params?: Request;
    abortSignal?: GenericAbortSignal | undefined;
    responseType?: 'json' | 'blob' | 'arraybuffer' | 'text';
  }): Promise<Response> {
    const clonedParams = structuredClone(params);
    for (const param in clonedParams) {
      if (clonedParams[param] === undefined || !String(clonedParams[param]))
        delete clonedParams[param];
    }

    try {
      const { data } = await axiosInstance(path, {
        method,
        data: body,
        params: clonedParams,
        signal: abortSignal,
        responseType
      });
      return data;
    } catch (e: unknown) {
      const error = e as CustomAxiosError;

      const statusCode =
        error?.response?.data?.statusCode || error?.response?.status;
      let message = 'Произошла ошибка. Попробуйте позже';
      if (
        statusCode &&
        statusCode !== 404 &&
        statusCode < 500 &&
        error?.response?.data?.message
      ) {
        if (Array.isArray(error.response.data.message))
          message = error.response.data.message.join('\n');
        else message = error.response.data.message;
      }

      throw {
        message,
        statusCode,
        isCancelled: axios.isCancel(e)
      };
    }
  }

  //DICTIONARY
  getOptimizedDictionaries(
    dictToFind: DictTypesKeys[]
  ): Promise<
    Array<{ name: DictTypes; content: GetDictContentType<DictTypes>[] }>
  > {
    return this.call({
      path: `dictionaries/getBulk`,
      method: 'post',
      body: {
        dicts: dictToFind
      }
    });
  }

  getCitiesForFilter(
    type: 'all' | 'dealers'
  ): Promise<GetDictContentType<DictTypes.Cities>[]> {
    return this.call({ path: `requests/cities/getForFilter/${type}` });
  }

  downloadFile(
    uuid: string
  ): Promise<{ base64: string; name: string; blob?: () => Promise<Blob> }> {
    return this.call({ path: `files/${uuid}/downloadmanually` });
  }
}

export const apiConnector = new ApiConnector();
